import { getRouteApi, useNavigate, useSearch } from '@tanstack/react-router'

import { usePostHog } from '@posthog/react'
import { format } from 'date-fns'
import { X } from 'lucide-react'

import { useUpdateURL } from '@/hooks/use-update-url'
import { cn } from '@/utils/ui/styles'

import { parseUTCToLocalDate } from '@/shared/datetime/utils/format'
import { toDisplayName } from '@/shared/parsing/utils/text'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

const Route = getRouteApi('/_dashboard/air/_layout/case-management')

type CaseFilterListProps = {
	className?: string
}

const FILTER_LABELS: Record<string, string> = {
	status: 'Status',
	severity: 'Severity',
	source: 'Source',
	type: 'Type',
	investigation: 'Investigation',
}

/**
 * Displays active filters (column filters + date filter) as removable badges
 */
export const CaseFilterList = ({ className }: CaseFilterListProps) => {
	const navigate = useNavigate()
	const { updateURL } = useUpdateURL()
	const posthog = usePostHog()

	// Get column filters from URL
	const search = useSearch({ strict: false }) as {
		filters?: Record<string, string>
	}
	const filters = search.filters ?? {}

	// Skip empty-value entries (e.g. a stale `?filters[category]=`) so we never
	// render a dangling "key:" badge.
	const filterEntries = Object.entries(filters).filter(([, value]) =>
		Boolean(value),
	)
	const activeFilters = Object.fromEntries(filterEntries)

	// Get date range from URL
	const openedAfter = Route.useSearch({ select: (sr) => sr.openedAfter })
	const openedBefore = Route.useSearch({ select: (sr) => sr.openedBefore })

	const handleRemoveFilter = (key: string) => {
		const { [key]: _removed, ...nextFilters } = activeFilters

		navigate({
			to: '.',
			search: (prev) => ({
				...prev,
				filters: Object.keys(nextFilters).length > 0 ? nextFilters : undefined,
			}),
		})
	}

	const handleRemoveDateFilter = (field: 'openedAfter' | 'openedBefore') => {
		updateURL({ search: { [field]: undefined } })
		posthog.capture('air_case_management_date_filter', {
			action: 'clear_badge',
			field,
		})
	}

	// Format date for display, tolerating malformed URL values.
	const formatDate = (value: string | undefined) => {
		if (!value) return null
		try {
			return format(parseUTCToLocalDate(value), 'MMM d, yyyy')
		} catch {
			return null
		}
	}

	const formattedAfter = formatDate(openedAfter)
	const formattedBefore = formatDate(openedBefore)

	const hasFilters =
		filterEntries.length > 0 || formattedAfter || formattedBefore

	if (!hasFilters) return null

	return (
		<div
			className={cn(
				'flex min-h-4 w-full flex-row flex-wrap items-center gap-2',
				className,
			)}
		>
			<p className="text-muted-foreground text-sm">Filters:</p>

			{/* Column filter badges */}
			{filterEntries.map(([key, value]) => (
				<Badge key={key} className="flex flex-row items-center gap-2">
					{/* Same reason as `TableFilterList`: the URL carries raw wire values
					    for the enum-derived groups, which CSS `capitalize` cannot
					    humanise. */}
					{FILTER_LABELS[key] ?? toDisplayName(key)}: {toDisplayName(value)}
					<Button
						variant="ghost"
						size="icon"
						className="size-4"
						onClick={() => handleRemoveFilter(key)}
						title={`Remove ${key} filter`}
					>
						<X className="size-4" />
					</Button>
				</Badge>
			))}

			{/* Opened-after badge */}
			{formattedAfter && (
				<Badge className="flex flex-row items-center gap-2 capitalize">
					Opened after: {formattedAfter}
					<Button
						variant="ghost"
						size="icon"
						className="size-4"
						onClick={() => handleRemoveDateFilter('openedAfter')}
						title="Remove opened after filter"
					>
						<X className="size-4" />
					</Button>
				</Badge>
			)}

			{/* Opened-before badge */}
			{formattedBefore && (
				<Badge className="flex flex-row items-center gap-2 capitalize">
					Opened before: {formattedBefore}
					<Button
						variant="ghost"
						size="icon"
						className="size-4"
						onClick={() => handleRemoveDateFilter('openedBefore')}
						title="Remove opened before filter"
					>
						<X className="size-4" />
					</Button>
				</Badge>
			)}
		</div>
	)
}
